import { CoreApi, Product, ProductPartial, ProductType } from '@inter-ikea-storageone/range';
import {
  isFrameSuitableForPullOutInterior,
  isSlidingDoorLeftFrontLayout,
  isSlidingDoorRightFrontLayout
} from '@inter-ikea-storageone/range-platsa';
import { ComponentIds, Entity, HierarchyUtils, TransformUtils } from '@inter-ikea-core/core';
import { CommunicationSizeComponent } from '@inter-ikea-core/core';
import { matchHandleSizeToFront as matchHandleSizeToFrontShared } from '../../shared/filters/selectedMenuProductFilters';
import { getEntityConnectorsThatOffer } from 'rangeConfig/shared/utils/connectors';

export const frameCanHaveInteriorSuggestion = (
  coreApi: CoreApi,
  selectedProduct: Product
) => () => {
  return ![
    ProductType.PLATSA_OPEN_FRAME_SHELVES,
    ProductType.PLATSA_OPEN_FRAME_HANGING,
    ProductType.PLATSA_OPEN_FRAME_SHOES
  ].includes(selectedProduct.type);
};

export const filterSuggestionsForSlidingDoors = (
  coreApi: CoreApi,
  selectedProduct: Product,
  selectedEntity?: Entity
) => (product: Product) => {
  if (!selectedEntity) {
    return false;
  }
  const slidingDoor = coreApi.entities.getChildrenWithProductType(
    selectedEntity,
    ProductType.PLATSA_SLIDING_DOOR_ASM
  )[0];

  const typesInProduct = product.parts
    .map(part => {
      return part.product?.type;
    })
    .filter((type): type is ProductType => !!type);

  if (slidingDoor && !isFrameSuitableForPullOutInterior(selectedEntity, coreApi)) {
    return !(
      typesInProduct.includes(ProductType.PLATSA_WIRE_BASKET_ASM) ||
      typesInProduct.includes(ProductType.PLATSA_DRAWER_ASM)
    );
  } else if (slidingDoor) {
    return !typesInProduct.includes(ProductType.PLATSA_DRAWER_ASM);
  }
  return true;
};

export const matchInteriorDepth = (coreApi: CoreApi, selectedProduct: Product | ProductPartial) => (
  product: Product | ProductPartial
) => {
  if (product.type === ProductType.PLATSA_SHOE_SHELF) {
    // Platsa shoe shelf fits any depth of Platsa frame
    return true;
  }
  return product.communicatedDimensions.depth === selectedProduct.communicatedDimensions.depth;
};

export const matchFrontType = (coreApi: CoreApi, selectedProduct: Product) => (
  product: Product
) => {
  if (selectedProduct.type === ProductType.PLATSA_SLIDING_DOOR_ASM) {
    return false;
  }
  return selectedProduct.type === product.type;
};

export const matchFrontLayoutToFrame = (
  coreApi: CoreApi,
  selectedProduct: Product,
  selectedEntity?: Entity
) => (product: Product) => {
  if (!selectedEntity) return false;
  const frame = HierarchyUtils.findWithComponentInUpwardsHierarchy(
    selectedEntity,
    ComponentIds.FRAME_COMPONENT_ID
  );
  if (!frame) return false;

  const alignedFrames = coreApi.entities.getAlignedFrames(
    frame,
    'align-frame-left',
    'align-frame-right',
    true,
    true
  );
  if (
    (isSlidingDoorLeftFrontLayout(product) && !alignedFrames.left) ||
    (isSlidingDoorRightFrontLayout(product) && !alignedFrames.right)
  ) {
    return false;
  }
  const frameSize = frame.getComponent<CommunicationSizeComponent>(
    ComponentIds.COMMUNICATION_SIZE_COMPONENT_ID
  )?.size;

  return (
    product.communicatedDimensions.width === frameSize?.width &&
    product.communicatedDimensions.height === frameSize?.height &&
    product.communicatedDimensions.depth === frameSize?.depth
  );
};

export const matchHandleSizeToFront = (
  coreApi: CoreApi,
  selectedProduct: Product | ProductPartial,
  selectedEntity?: Entity
) => (product: Product | ProductPartial) => {
  if (
    selectedEntity &&
    selectedEntity?.hasComponent(ComponentIds.DOOR_COMPONENT_ID) &&
    coreApi.entities.getEntityComSize(selectedEntity)?.height === 1200
  ) {
    // 120cm doors in PLATSA have one handle connector 30cm from edge so need to check that handle can fit there (less than 60cm high)
    const handleHeight = product.communicatedDimensions.height;
    return handleHeight < 600;
  } else {
    return matchHandleSizeToFrontShared(coreApi, selectedProduct, selectedEntity)(product);
  }
};

/**
 * Don't display interior suggestions with drawers in frames above 160cm
 */
export const matchInteriorSuggestionToFramePlacement = (
  coreApi: CoreApi,
  selectedProduct: Product | ProductPartial,
  selectedEntity?: Entity
) => (product: Product) => {
  if (!selectedEntity || !selectedEntity.hasComponent(ComponentIds.FRAME_COMPONENT_ID)) {
    return false;
  }
  const framePlacement = TransformUtils.worldPosition(selectedEntity).Y;
  const typesInProduct = product.parts
    .map(part => {
      return part.product?.type;
    })
    .filter((type): type is ProductType => !!type);
  return typesInProduct.includes(ProductType.PLATSA_DRAWER_ASM) && framePlacement > 1600
    ? false
    : true;
};

export const matchOrganizerToSelectedInterior = (
  coreApi: CoreApi,
  selectedProduct: Product | ProductPartial
) => (product: Product | ProductPartial) => {
  if (selectedProduct.type === ProductType.PLATSA_SHELF) {
    return (
      (product.type === ProductType.PLATSA_SHELF_AND_DRAWER_ORGANIZER ||
        product.type === ProductType.PLATSA_SHELF_ORGANIZER) &&
      ((product.communicatedDimensions.width < selectedProduct.communicatedDimensions.width &&
        product.communicatedDimensions.depth < selectedProduct.communicatedDimensions.depth) ||
        (product.communicatedDimensions.depth < selectedProduct.communicatedDimensions.width &&
          product.communicatedDimensions.width < selectedProduct.communicatedDimensions.depth))
    );
  } else if (selectedProduct.type === ProductType.PLATSA_DRAWER_ASM) {
    return (
      product.type === ProductType.PLATSA_SHELF_AND_DRAWER_ORGANIZER &&
      ((product.communicatedDimensions.width < selectedProduct.communicatedDimensions.width &&
        product.communicatedDimensions.depth < selectedProduct.communicatedDimensions.depth) ||
        (product.communicatedDimensions.depth < selectedProduct.communicatedDimensions.width &&
          product.communicatedDimensions.width < selectedProduct.communicatedDimensions.depth))
    );
  } else if (
    selectedProduct.type === ProductType.PLATSA_CLOTH_RAIL_55_W_HOOKS_ASM ||
    selectedProduct.type === ProductType.PLATSA_CLOTH_RAIL_55
  ) {
    return product.type === ProductType.PLATSA_CLOTH_RAIL_ORGANIZER;
  }
  return false;
};

export const canHaveLight = (
  _coreApi: CoreApi,
  _selectedProduct: Product,
  selectedEntity?: Entity
) => (_product: Product) => {
  if (!selectedEntity) return false;

  const connectors = getEntityConnectorsThatOffer(selectedEntity, 'light-strip');
  return !!connectors.length;
};
